// Image preparation: decode, respect EXIF orientation, downscale, re-encode as
// JPEG. Accepting only image/jpeg and image/png in the picker makes iOS convert
// HEIC on its own.

export interface PreparedImage {
  bytes: Uint8Array;
  width: number;
  height: number;
  contentType: 'image/jpeg';
}

async function encode(bitmap: ImageBitmap, maxEdge: number): Promise<PreparedImage> {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) {
    throw new Error('no 2d canvas');
  }
  context.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => (result !== null ? resolve(result) : reject(new Error('encode failed'))),
      'image/jpeg',
      0.85,
    );
  });
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width, height, contentType: 'image/jpeg' };
}

export async function prepareImage(file: Blob, maxEdge = 2048): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    return await encode(bitmap, maxEdge);
  } finally {
    bitmap.close();
  }
}

/** The error catalogue requires the client to be able to shrink and retry when
 *  the server answers `media_too_large`. */
export async function shrinkImage(bytes: Uint8Array, contentType: string): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(new Blob([bytes as BlobPart], { type: contentType }));
  try {
    return await encode(bitmap, Math.max(256, Math.floor(Math.max(bitmap.width, bitmap.height) / 2)));
  } finally {
    bitmap.close();
  }
}
